import { useState } from 'react';
import { cn } from '../../lib/utils';
import { Button, Spinner } from '../ui';

/**
 * SkillQuestion - Lucky Number Design System
 *
 * Displays a skill-based question with 3 options.
 * Users must answer correctly to proceed with entry.
 * This makes the raffle legally a "game of skill" in Ireland.
 *
 * Features petrol borders, electric selection, accent success.
 * Works on both cream (light) and midnight (dark) backgrounds.
 *
 * Security Note: The correct answer validation happens SERVER-SIDE
 * in the purchaseEntry Cloud Function. This component only handles
 * the UI - it does NOT know the correct answer.
 */
export default function SkillQuestion({
  question,
  loading = false,
  error = null,
  onAnswer,
  onSkip,
  disabled = false,
  light = false, // Use on cream backgrounds
}) {
  const [selectedOption, setSelectedOption] = useState(null);
  const [answerState, setAnswerState] = useState('idle'); // 'idle' | 'checking' | 'correct' | 'incorrect'

  const handleSelect = (optionIndex) => {
    if (disabled || answerState === 'checking' || answerState === 'correct') return;

    setSelectedOption(optionIndex);
    setAnswerState('idle');
  };

  const handleSubmit = async () => {
    if (selectedOption === null || answerState === 'checking') return;

    setAnswerState('checking');

    try {
      // Server validates the answer
      const isCorrect = await onAnswer(selectedOption);

      if (isCorrect) {
        setAnswerState('correct');
      } else {
        setAnswerState('incorrect');
        // Reset after showing incorrect feedback
        setTimeout(() => {
          setSelectedOption(null);
          setAnswerState('idle');
        }, 1500);
      }
    } catch (err) {
      setAnswerState('idle');
      // Error is handled by parent component
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Spinner size="lg" variant={light ? 'default' : 'accent'} />
        <p className={cn(
          'mt-4',
          light ? 'text-midnight/60' : 'text-cream/60'
        )}>Loading question...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-error/20 mb-4">
          <ErrorIcon className="h-6 w-6 text-error" />
        </div>
        <p className={cn(
          'font-medium',
          light ? 'text-midnight' : 'text-cream'
        )}>Failed to load question</p>
        <p className={cn(
          'text-sm mt-1',
          light ? 'text-midnight/60' : 'text-cream/60'
        )}>{error}</p>
      </div>
    );
  }

  if (!question) {
    return null;
  }

  return (
    <div className="space-y-6">
      {/* Question header */}
      <div className="text-center">
        <div className={cn(
          'inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-bold uppercase tracking-wider mb-4',
          light
            ? 'bg-petrol/10 text-petrol'
            : 'bg-electric/20 text-electric'
        )}>
          <BrainIcon className="w-4 h-4" />
          Skill Question
        </div>
        <h3 className={cn(
          'text-lg font-display font-semibold',
          light ? 'text-midnight' : 'text-cream'
        )}>
          {question.question}
        </h3>
        <p className={cn(
          'text-sm mt-2',
          light ? 'text-midnight/60' : 'text-cream/60'
        )}>
          Answer correctly to continue with your entry
        </p>
      </div>

      {/* Options */}
      <div className="space-y-3">
        {question.options.map((option, index) => (
          <button
            key={index}
            type="button"
            onClick={() => handleSelect(index)}
            disabled={disabled || answerState === 'checking' || answerState === 'correct'}
            className={cn(
              'w-full p-4 rounded-xl border-2 text-left transition-all duration-200',
              'focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent',
              light ? 'focus:ring-offset-cream' : 'focus:ring-offset-midnight',
              // Default state
              selectedOption !== index && answerState !== 'correct' && (
                light
                  ? 'border-midnight/10 hover:border-midnight/20 bg-white'
                  : 'border-cream/10 hover:border-cream/20 bg-midnight-light'
              ),
              // Selected state
              selectedOption === index && answerState === 'idle' && (
                light
                  ? 'border-petrol bg-petrol/10'
                  : 'border-electric bg-electric/10'
              ),
              // Checking state
              selectedOption === index && answerState === 'checking' && (
                light
                  ? 'border-midnight/20 bg-midnight/5'
                  : 'border-cream/20 bg-cream/5'
              ),
              // Correct state
              answerState === 'correct' && selectedOption === index &&
                'border-success bg-success/10',
              // Incorrect state
              answerState === 'incorrect' && selectedOption === index &&
                'border-error bg-error/10',
              // Disabled
              'disabled:cursor-not-allowed disabled:opacity-60'
            )}
          >
            <div className="flex items-center gap-3">
              {/* Option indicator */}
              <div
                className={cn(
                  'w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0',
                  'border-2 font-bold font-mono text-sm',
                  selectedOption !== index && (
                    light ? 'border-midnight/20 text-midnight/50' : 'border-cream/20 text-cream/50'
                  ),
                  selectedOption === index && answerState === 'idle' && (
                    light
                      ? 'border-petrol text-petrol bg-white'
                      : 'border-electric text-electric bg-midnight'
                  ),
                  answerState === 'correct' && selectedOption === index && 'border-success text-success bg-midnight',
                  answerState === 'incorrect' && selectedOption === index && 'border-error text-error bg-midnight'
                )}
              >
                {answerState === 'correct' && selectedOption === index ? (
                  <CheckIcon className="w-5 h-5" />
                ) : answerState === 'incorrect' && selectedOption === index ? (
                  <XIcon className="w-5 h-5" />
                ) : (
                  String.fromCharCode(65 + index) // A, B, C
                )}
              </div>

              {/* Option text */}
              <span
                className={cn(
                  light ? 'text-midnight' : 'text-cream',
                  selectedOption === index && answerState === 'idle' && 'font-medium',
                  answerState === 'correct' && selectedOption === index && 'text-success font-medium',
                  answerState === 'incorrect' && selectedOption === index && 'text-error'
                )}
              >
                {option}
              </span>
            </div>
          </button>
        ))}
      </div>

      {/* Feedback messages */}
      {answerState === 'correct' && (
        <div className="flex items-center gap-2 text-success bg-success/10 p-4 rounded-xl border border-success/30">
          <CheckIcon className="w-5 h-5" />
          <span className="font-bold">Correct! Proceeding to payment...</span>
        </div>
      )}

      {answerState === 'incorrect' && (
        <div className="flex items-center gap-2 text-error bg-error/10 p-4 rounded-xl border border-error/30">
          <XIcon className="w-5 h-5" />
          <span className="font-bold">Incorrect. Try again!</span>
        </div>
      )}

      {/* Submit button */}
      {answerState !== 'correct' && (
        <Button
          fullWidth
          onClick={handleSubmit}
          disabled={selectedOption === null || answerState === 'checking'}
          loading={answerState === 'checking'}
        >
          {answerState === 'checking' ? 'Checking...' : 'Submit Answer'}
        </Button>
      )}
    </div>
  );
}

// Icons
function BrainIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
      />
    </svg>
  );
}

function CheckIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
  );
}

function XIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

function ErrorIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}
